import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Home as HomeIcon, Building2, HeartHandshake, ShieldCheck, RefreshCw, MessageCircleHeart, ArrowRight, Star, Sparkles } from "lucide-react";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { BookingForm } from "@/components/BookingForm";
import { CompanionQuiz } from "@/components/CompanionQuiz";
import { IMAGES, PHONE, PHONE_HREF, BBB_URL, BBB_SEAL } from "@/lib/images";
import { TrustBand } from "@/components/TrustBand";

const MARQUEE_ITEMS = [
  "Companionship",
  "Peaceful Walks",
  "Tea & Story Sharing",
  "Appointment Escorts",
  "Crafts & Games",
  "Facility Visits",
  "Peace of Mind",
];

const SETTINGS = [
  {
    icon: HomeIcon,
    title: "At Home",
    text: "Friendly visits right in the comfort of your loved one's own home — conversation, hobbies, walks and warm company.",
    testid: "setting-card-home",
  },
  {
    icon: Building2,
    title: "Assisted Living",
    text: "One-on-one companionship inside assisted living residences — a familiar face who shows up just for them.",
    testid: "setting-card-assisted",
  },
  {
    icon: HeartHandshake,
    title: "Care Facilities",
    text: "Regular visits to care facilities across Calgary, so no one ever feels forgotten between family visits.",
    testid: "setting-card-facility",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Tell us about them",
    text: "A free, no-pressure consultation where we learn about your loved one's personality, interests and routine.",
  },
  {
    num: "02",
    title: "Meet the perfect match",
    text: "We hand-pick a vetted companion whose personality and interests genuinely click with theirs.",
  },
  {
    num: "03",
    title: "Joy, on repeat",
    text: "Regular visits with the same companion — plus warm updates to the family after every visit.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Mom lights up on companion days. It's the first time in years I've seen her look forward to something every single week.",
    name: "Karen T.",
    relation: "Daughter of a client, NW Calgary",
    testid: "testimonial-card-1",
  },
  {
    quote:
      "Dad's companion shares his love of chess and old war stories. We finally stopped worrying about him being lonely.",
    name: "Michael R.",
    relation: "Son of a client, Lake Bonavista",
    testid: "testimonial-card-2",
  },
  {
    quote:
      "The updates after every visit mean the world. Living in Toronto, I still feel close to what matters most.",
    name: "Sandra L.",
    relation: "Daughter living away",
    testid: "testimonial-card-3",
  },
];

export default function Home() {
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 600], [0, 90]);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-brand-soft blur-3xl" />
        <div className="pointer-events-none absolute -left-40 bottom-0 h-[26rem] w-[26rem] rounded-full bg-sage-soft blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10 lg:px-8 lg:pb-28 lg:pt-24">
          <div>
            <FadeIn>
              <p className="eyebrow mb-5" data-testid="hero-eyebrow">
                Calgary's Companionship Care for Seniors
              </p>
            </FadeIn>
            <h1 className="mb-6 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-espresso sm:text-5xl lg:text-6xl" data-testid="hero-heading">
              <MaskedLine delay={0.1}>Companionship that</MaskedLine>
              <MaskedLine delay={0.25}>
                feels like <em className="text-brand">family.</em>
              </MaskedLine>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mb-9 max-w-xl text-lg leading-relaxed text-warmgray sm:text-xl" data-testid="hero-subheading">
                Off Our Rockers brings joyful, non-medical companionship to seniors across Calgary — at
                home, in assisted living, or in care facilities — with reassurance and peace of mind
                for the whole family.
              </p>
            </FadeIn>
            <FadeIn delay={0.65} className="flex flex-col gap-4 sm:flex-row">
              <Link to="/contact" data-testid="hero-book-now-button" className="btn-primary">
                Book a Free Consultation <ArrowRight className="h-5 w-5" />
              </Link>
              <a href={PHONE_HREF} data-testid="hero-call-phone-button" className="btn-outline">
                <Phone className="h-5 w-5" /> {PHONE}
              </a>
            </FadeIn>
            <FadeIn delay={0.8} className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3 text-base font-semibold text-espresso/70">
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-sage" /> Fully vetted companions
              </span>
              <span className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-sage" /> Same companion every visit
              </span>
              <span className="flex items-center gap-2">
                <MessageCircleHeart className="h-5 w-5 text-sage" /> Family updates included
              </span>
            </FadeIn>
          </div>

          <FadeIn delay={0.35} className="relative">
            <motion.div style={{ y: heroImgY }} className="relative">
              <div className="overflow-hidden rounded-b-[2.5rem] rounded-t-[12rem] border-8 border-white shadow-2xl shadow-espresso/20">
                <img
                  src={IMAGES.heroTea}
                  alt="A senior woman laughing over tea with her companion"
                  className="aspect-[4/5] w-full object-cover"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 max-w-[16rem] rounded-2xl border border-espresso/10 bg-white p-5 shadow-xl shadow-espresso/15 sm:-left-10">
                <div className="mb-2 flex gap-1" aria-label="Five star rating">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className="text-base font-semibold leading-snug text-espresso">
                  "It's like a dear friend dropping by — every single week."
                </p>
                <p className="mt-1.5 text-sm text-warmgray">A Calgary family</p>
              </div>
            </motion.div>
          </FadeIn>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <TrustBand />

      {/* Where we care */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal className="mb-14 max-w-2xl">
          <p className="eyebrow mb-4">Where We Care</p>
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
            Wherever your loved one calls home
          </h2>
        </Reveal>
        <div className="grid gap-8 md:grid-cols-3">
          {SETTINGS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <article
                data-testid={s.testid}
                className="group h-full rounded-3xl border border-espresso/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-espresso/10"
              >
                <div className="mb-5 inline-flex rounded-2xl bg-sage-soft p-4 transition-colors duration-300 group-hover:bg-brand-soft">
                  <s.icon className="h-8 w-8 text-sage transition-colors duration-300 group-hover:text-brand" />
                </div>
                <h3 className="mb-3 font-serif text-xl font-medium text-espresso sm:text-2xl">{s.title}</h3>
                <p className="text-base leading-relaxed text-warmgray sm:text-lg">{s.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How matching works */}
      <section className="bg-sand">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:py-28 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-[2.5rem] border-8 border-white shadow-2xl shadow-espresso/15">
              <img
                src={IMAGES.aboutWalk}
                alt="Two companions on a mountain path overlooking the Rockies"
                className="aspect-[4/5] w-full object-cover"
                loading="lazy"
                data-testid="matching-image"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="eyebrow mb-4">How Matching Works</p>
              <h2 className="mb-10 font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
                Matched with care, not by chance
              </h2>
            </Reveal>
            <ol className="space-y-8">
              {STEPS.map((step, i) => (
                <Reveal key={step.num} delay={i * 0.12}>
                  <li className="flex gap-5" data-testid={`matching-step-${i + 1}`}>
                    <span className="font-serif text-4xl font-bold text-brand/40">{step.num}</span>
                    <div>
                      <h3 className="mb-1.5 font-serif text-xl font-medium text-espresso sm:text-2xl">{step.title}</h3>
                      <p className="text-base leading-relaxed text-warmgray sm:text-lg">{step.text}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
            <Reveal delay={0.35} className="mt-10">
              <Link to="/services" data-testid="matching-learn-more-button" className="btn-outline">
                Explore Our Services <ArrowRight className="h-5 w-5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Companion Match Quiz */}
      <section id="quiz" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <p className="eyebrow mb-4 flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> The Match Quiz
            </p>
            <h2 className="mb-5 font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
              Find their perfect companion in one minute
            </h2>
            <p className="max-w-md text-lg leading-relaxed text-warmgray">
              Four easy questions about your loved one's world — and we'll show you the kind of
              companion and visits we'd match them with. No obligation, just a little insight.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <CompanionQuiz />
          </Reveal>
        </div>
      </section>

      {/* Bento — peace of mind */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="mb-14 max-w-2xl">
            <p className="eyebrow mb-4">For the Family</p>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
              Peace of mind, built into every visit
            </h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3" data-testid="benefits-bento">
            <Reveal className="md:col-span-2 md:row-span-2">
              <div className="group relative h-full min-h-[22rem] overflow-hidden rounded-3xl shadow-lg shadow-espresso/15">
                <img
                  src={IMAGES.communityLaugh}
                  alt="Seniors laughing together outdoors"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 via-espresso/20 to-transparent" />
                <div className="absolute bottom-0 p-8">
                  <h3 className="mb-2 font-serif text-2xl font-semibold text-white sm:text-3xl">
                    Joy you can actually see
                  </h3>
                  <p className="max-w-md text-base text-cream/90 sm:text-lg">
                    Real connection, real laughter — and photos and notes shared with the family after
                    visits, so you never miss a moment.
                  </p>
                </div>
              </div>
            </Reveal>
            {[
              {
                icon: MessageCircleHeart,
                title: "Updates after every visit",
                text: "A warm note to the family after each visit — how they felt, what they did, what made them smile.",
                testid: "bento-updates-card",
              },
              {
                icon: ShieldCheck,
                title: "Vetted & insured",
                text: "Every companion passes background checks, reference checks and our own compassion interview.",
                testid: "bento-vetted-card",
              },
              {
                icon: RefreshCw,
                title: "The same familiar face",
                text: "Consistency builds trust. Your loved one sees the same companion, visit after visit.",
                testid: "bento-consistency-card",
              },
            ].map((b, i) => (
              <Reveal key={b.title} delay={0.1 + i * 0.1}>
                <article
                  data-testid={b.testid}
                  className="h-full rounded-3xl border border-espresso/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-espresso/10"
                >
                  <b.icon className="mb-4 h-8 w-8 text-brand" />
                  <h3 className="mb-2 font-serif text-xl font-medium text-espresso sm:text-2xl">{b.title}</h3>
                  <p className="text-base leading-relaxed text-warmgray">{b.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-sage-soft/60">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="mb-14 max-w-2xl">
            <p className="eyebrow mb-4">Kind Words</p>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
              From families like yours
            </h2>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <figure
                  data-testid={t.testid}
                  className="flex h-full flex-col rounded-3xl border border-espresso/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-espresso/10"
                >
                  <div className="mb-4 flex gap-1" aria-label="Five star review">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="h-5 w-5 fill-gold text-gold" />
                    ))}
                  </div>
                  <blockquote className="flex-1 font-serif text-lg italic leading-relaxed text-espresso sm:text-xl">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-6 border-t border-espresso/10 pt-4">
                    <p className="text-base font-bold text-espresso">{t.name}</p>
                    <p className="text-sm text-warmgray">{t.relation}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" id="book">
        <div className="grid items-start gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <p className="eyebrow mb-4">Free Consultation</p>
            <h2 className="mb-5 font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
              Let's find their perfect companion
            </h2>
            <p className="mb-8 max-w-md text-lg leading-relaxed text-warmgray">
              Tell us a little about your loved one and pick a time that suits you. We'll reach out
              to chat about their personality, interests and needs — and start finding the match.
            </p>
            <div className="overflow-hidden rounded-[2rem] border-8 border-white shadow-xl shadow-espresso/15">
              <img
                src={IMAGES.testimonialPortrait}
                alt="A smiling senior portrait"
                className="aspect-video w-full object-cover"
                loading="lazy"
                data-testid="booking-side-image"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-espresso/10 bg-white p-6 shadow-xl shadow-espresso/10 sm:p-10">
              <BookingForm idPrefix="home-booking-form" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
